/**
 * Facade over the generated **internal** admin client (`../generated-internal/admin`).
 *
 * The `/admin/*` surface is platform-admin scoped (super OR app admin), not
 * org-scoped, so — unlike the public facades in this directory — these hooks
 * take no branded {@link OrgId}. They wrap the orval-generated internal hooks
 * with the project's admin query keys + invalidation, giving web admin pages a
 * stable, typed surface instead of hand-written fetchers.
 *
 * Import from `@reflex/ui/client/api/admin`. Only admin UI (gated behind
 * `RequireSuperAdmin` / platform-admin checks) should reach for these.
 */
import { ApiError } from '../http';
import { queryKeys } from '../query-keys';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAdminUserErasure,
  useEraseAdminUser,
  useReviewAdminUserErasure,
  useDeleteAdminUserProvider,
  useGetAdminUserOrganizations,
  useGrantAppAdmin,
  useListAdminOrganizations,
  useListAdminUsers,
  useListAppAdmins,
  useRevokeAppAdmin,
  useSetAdminUserStatus,
} from '../generated-internal/admin';

/** Query key for the platform app-admins list. Matches `web` `queryKeys.adminAppAdmins`. */
export const appAdminsKey = ['admin', 'app-admins'] as const;

/** Pagination + search params for the admin users list. */
export interface AdminUsersParams {
  page: number;
  pageSize: number;
  q: string;
}

/** Per-query key for the paginated admin users list. Matches `web` `queryKeys.adminUsersPage`. */
export const adminUsersKey = (params: AdminUsersParams) => ['admin', 'users', params] as const;

/**
 * Paginated, searchable list of every registered user (platform-admin).
 * `keepPreviousData` gives a smooth page transition, matching the existing
 * admin users page.
 */
export function useAdminUsers(params: AdminUsersParams) {
  return useListAdminUsers(params, {
    query: { queryKey: adminUsersKey(params), placeholderData: keepPreviousData },
  });
}

/** Pagination + search params for the admin organizations list. */
export interface AdminOrganizationsParams {
  page: number;
  pageSize: number;
  q: string;
}

/** Per-query key for the admin organizations list. Matches `web` `queryKeys.adminOrganizationsPage`. */
export const adminOrganizationsKey = (params: AdminOrganizationsParams) =>
  ['admin', 'organizations', params] as const;

/** Paginated, searchable list of every organization (platform-admin). */
export function useAdminOrganizations(params: AdminOrganizationsParams) {
  return useListAdminOrganizations(params, {
    query: { queryKey: adminOrganizationsKey(params), placeholderData: keepPreviousData },
  });
}

/**
 * Query key for a user's org memberships in the admin console. Matches `web`
 * `queryKeys.adminUserOrganizations` (note the singular `user` segment).
 */
export const adminUserOrganizationsKey = (userId: string) =>
  ['admin', 'user', userId, 'organizations'] as const;

/** List every holder of the assignable platform-admin role. */
export function useAppAdmins() {
  return useListAppAdmins({ query: { queryKey: appAdminsKey } });
}

/** Grant the platform-admin role, invalidating the app-admins list on success. */
export function useGrantAppAdminMutation() {
  const qc = useQueryClient();
  return useGrantAppAdmin({
    mutation: { onSuccess: () => void qc.invalidateQueries({ queryKey: appAdminsKey }) },
  });
}

/** Revoke the platform-admin role, invalidating the app-admins list on success. */
export function useRevokeAppAdminMutation() {
  const qc = useQueryClient();
  return useRevokeAppAdmin({
    mutation: { onSuccess: () => void qc.invalidateQueries({ queryKey: appAdminsKey }) },
  });
}

/**
 * Toggle a user's lifecycle status (`active` / `disabled`). Invalidates the
 * app-admins list since disabling can interact with platform-admin membership.
 */
export function useSetAdminUserStatusMutation() {
  const qc = useQueryClient();
  return useSetAdminUserStatus({
    mutation: { onSuccess: () => void qc.invalidateQueries({ queryKey: appAdminsKey }) },
  });
}

/** Unlink any user's auth provider (admin override). */
export function useDeleteAdminUserProviderMutation() {
  return useDeleteAdminUserProvider();
}

/** List the organizations a user belongs to (admin cross-tenant lookup). */
export function useAdminUserOrganizations(userId: string) {
  return useGetAdminUserOrganizations(userId, {
    query: { queryKey: adminUserOrganizationsKey(userId), enabled: Boolean(userId) },
  });
}

/** A missing receipt is expected before erasure; all other failures stay visible. */
export const adminUserErasureKey = (userId: string) =>
  ['admin', 'user', userId, 'erasure'] as const;

export function useAdminUserErasure(userId: string, enabled = true) {
  return useQuery({
    queryKey: adminUserErasureKey(userId),
    enabled: enabled && Boolean(userId),
    retry: false,
    queryFn: async () => {
      try {
        return await getAdminUserErasure(userId);
      } catch (error) {
        if (
          error instanceof ApiError &&
          error.status === 404 &&
          error.code === 'erasure_not_found'
        ) {
          return null;
        }
        throw error;
      }
    },
  });
}

export function useEraseAdminUserMutation() {
  const qc = useQueryClient();
  return useEraseAdminUser({
    mutation: {
      onSuccess: (receipt, { userId }) => {
        qc.setQueryData(adminUserErasureKey(userId), receipt);
        // Drop pre-erasure profile/private data rather than showing it during a refetch.
        void qc.resetQueries({ queryKey: queryKeys.user(userId) });
        void qc.resetQueries({ queryKey: queryKeys.userProvidersById(userId) });
        void qc.resetQueries({ queryKey: queryKeys.adminUsers });
        void qc.invalidateQueries({ queryKey: adminUserOrganizationsKey(userId) });
      },
    },
  });
}

export function useReviewAdminUserErasureMutation() {
  const qc = useQueryClient();
  return useReviewAdminUserErasure({
    mutation: {
      onSuccess: (receipt, { userId }) => qc.setQueryData(adminUserErasureKey(userId), receipt),
    },
  });
}
